/**
 * Shared interactive UI primitives — sl-interactive v2
 *
 *  GlowCard    — card touch spotlight (fires on tap, not just drag)
 *  TapButton   — bounce on tap · grow on hold · explode on very long hold
 *  Global tap  — injects bounce on ALL <button> elements automatically
 */
import React from 'react';
import ReactDOM from 'react-dom';

const BASE = process.env.REACT_APP_API_URL || '/api';

// ── CSS — injected once into <head> ──────────────────────────────────────────
(function injectCSS() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('sl-ux-css')) return;
  const s = document.createElement('style');
  s.id = 'sl-ux-css';
  s.textContent = `
    /* pill/card bounce on tap */
    @keyframes _dpb {
      0%   { transform: scale(1); }
      18%  { transform: scale(.80); }
      52%  { transform: scale(1.12); }
      74%  { transform: scale(.96); }
      100% { transform: scale(1); }
    }
    /* explosion — burst out then collapse */
    @keyframes _xBurst {
      0%   { transform: scale(1);    opacity: 1; }
      28%  { transform: scale(1.55); opacity: 0.8; }
      60%  { transform: scale(0.25); opacity: 0.3; }
      100% { transform: scale(0);    opacity: 0; }
    }
    /* shockwave ring */
    @keyframes _xRing {
      0%   { transform: scale(0.3); opacity: 0.9; }
      100% { transform: scale(3.2); opacity: 0; }
    }
    /* flash overlay */
    @keyframes _xFlash {
      0%,40% { opacity: 0.55; }
      100%   { opacity: 0; }
    }
    /* grow back after explosion */
    @keyframes _xGrow {
      0%   { transform: scale(0);    opacity: 0; }
      50%  { transform: scale(1.18); opacity: 1; }
      72%  { transform: scale(0.94); }
      86%  { transform: scale(1.05); }
      100% { transform: scale(1);    opacity: 1; }
    }
    /* particle fly-out */
    @keyframes _xPart {
      0%   { opacity: 1; transform: translate(0,0) scale(1) rotate(0deg); }
      100% { opacity: 0; transform: translate(var(--tx),var(--ty)) scale(0) rotate(var(--rot)); }
    }
    /* toast slide-up */
    @keyframes _toast {
      0%   { transform: translateY(30px); opacity: 0; }
      15%  { transform: translateY(0);    opacity: 1; }
      75%  { transform: translateY(0);    opacity: 1; }
      100% { transform: translateY(-10px); opacity: 0; }
    }
    /* no text selection anywhere we handle touch */
    .sl-tap { user-select: none; -webkit-user-select: none; touch-action: manipulation; }
  `;
  document.head.appendChild(s);
})();

// ── Global bounce on ALL <button> elements ────────────────────────────────────
(function globalTap() {
  if (typeof document === 'undefined') return;
  document.addEventListener('pointerdown', e => {
    const btn = e.target.closest('button:not([disabled]):not([data-no-bounce])');
    if (!btn) return;
    // skip elements inside bottom/top nav
    if (btn.closest('[data-nav]')) return;
    btn.style.animation = 'none';
    // force reflow so the browser resets the animation
    // eslint-disable-next-line no-unused-expressions
    btn.getBoundingClientRect();
    btn.style.animation = '_dpb 0.38s cubic-bezier(.34,1.56,.64,1) both';
    btn.addEventListener('animationend', () => { btn.style.animation = ''; }, { once: true });
  }, { passive: true });
})();

// ── Helpers ───────────────────────────────────────────────────────────────────
function getUid()  { try { return localStorage.getItem('sl_user_id') || null; } catch { return null; } }
function getLang() { try { return localStorage.getItem('sl_lang') || 'nl'; }    catch { return 'nl'; } }
function getXplCount() { try { return parseInt(localStorage.getItem('sl_xpl') || '0', 10); } catch { return 0; } }
function incXplCount() { try { localStorage.setItem('sl_xpl', String(getXplCount() + 1)); } catch {} }

async function awardCredit() {
  try {
    const uid = getUid();
    if (!uid) return;
    const res = await fetch(`${BASE}/wallet/${encodeURIComponent(uid)}/credit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: 1, reason: 'easter_egg_explosion' }),
    });
    if (res.ok) {
      const d = await res.json();
      if (d.balance !== undefined) {
        try { localStorage.setItem('sl_wallet_balance', String(d.balance)); } catch {}
      }
    }
  } catch {}
}

// ── Discovery popup (first 2 times) ──────────────────────────────────────────
function DiscoveryPopup({ onClose }) {
  const nl = getLang() === 'nl';
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(8px)',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'linear-gradient(145deg,#1a1030,#0d1a2e)',
        border: '1.5px solid rgba(200,168,75,0.7)',
        borderRadius: 26, padding: '40px 30px', textAlign: 'center',
        maxWidth: 310, width: '88vw',
        boxShadow: '0 0 100px rgba(200,168,75,0.4), 0 30px 80px rgba(0,0,0,0.95)',
        animation: '_xGrow 0.55s cubic-bezier(.34,1.56,.64,1) both',
      }}>
        <div style={{ fontSize: 56, marginBottom: 10 }}>🔮</div>
        <div style={{ color: '#C8A84B', fontSize: 20, fontWeight: 700, fontFamily: 'Georgia,serif', marginBottom: 10 }}>
          {nl ? 'Verborgen Ontdekking!' : 'Hidden Discovery!'}
        </div>
        <div style={{ color: '#8899BB', fontSize: 13, lineHeight: 1.75, marginBottom: 18 }}>
          {nl
            ? 'Je hebt een geheim gevonden door lang vast te houden. Je verdient een Focus Credit als beloning!'
            : 'You found a secret by holding long. You earn a Focus Credit as reward!'}
        </div>
        <div style={{ color: '#C8A84B', fontSize: 32, fontWeight: 700, marginBottom: 22, letterSpacing: 1 }}>
          +0.01 🎯
        </div>
        <button data-no-bounce onClick={onClose} style={{
          background: 'linear-gradient(135deg,#C8A84B,#a87d30)',
          color: '#0a1220', border: 'none', borderRadius: 14,
          padding: '13px 32px', fontWeight: 700, fontSize: 15, cursor: 'pointer',
        }}>
          ✨ {nl ? 'Geweldig!' : 'Awesome!'}
        </button>
      </div>
    </div>,
    document.body
  );
}

// ── Small credit toast (after 2 popups) ───────────────────────────────────────
function CreditToast({ onDone }) {
  React.useEffect(() => {
    const t = setTimeout(onDone, 2600);
    return () => clearTimeout(t);
  }, [onDone]);
  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', bottom: 90, left: '50%', transform: 'translateX(-50%)',
      zIndex: 99998, pointerEvents: 'none',
      background: 'linear-gradient(135deg,rgba(200,168,75,0.95),rgba(168,125,48,0.95))',
      color: '#0a1220', borderRadius: 30, padding: '10px 22px',
      fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap',
      boxShadow: '0 4px 24px rgba(200,168,75,0.5)',
      animation: '_toast 2.6s ease both',
    }}>
      +0.01 🎯 Focus Credit
    </div>,
    document.body
  );
}

// ── Explosion particles ───────────────────────────────────────────────────────
const SHAPES  = ['circle', 'star', 'square'];
const PCOLS   = ['#C8A84B','#FFD700','#7B6FD0','#2A9E82','#D4873A','#C06090','#5B9ED0','#fff','#FF6B6B'];

function Particles() {
  const pts = React.useMemo(() => {
    const n = 22;
    return Array.from({ length: n }).map((_, i) => {
      const angle  = (i / n) * 360 + (i % 2 ? 8 : -5);
      const dist   = 50 + (i % 5) * 22;
      const rad    = (angle * Math.PI) / 180;
      const tx     = Math.round(Math.cos(rad) * dist);
      const ty     = Math.round(Math.sin(rad) * dist);
      const size   = 5 + (i % 4) * 3;
      const dur    = 0.38 + (i % 4) * 0.07;
      const delay  = (i % 3) * 0.04;
      const rot    = `${(i % 2 ? 1 : -1) * (90 + i * 12)}deg`;
      const shape  = SHAPES[i % 3];
      const color  = PCOLS[i % PCOLS.length];
      return { tx, ty, size, dur, delay, rot, shape, color };
    });
  }, []);

  return (
    <div aria-hidden style={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      zIndex: 100, overflow: 'visible',
    }}>
      {/* shockwave ring */}
      <div style={{
        position: 'absolute', left: '50%', top: '50%',
        width: 60, height: 60,
        marginLeft: -30, marginTop: -30,
        borderRadius: '50%',
        border: '3px solid rgba(200,168,75,0.9)',
        boxShadow: '0 0 20px rgba(200,168,75,0.6)',
        animation: '_xRing 0.55s ease-out both',
        pointerEvents: 'none',
      }} />
      {/* flash */}
      <div style={{
        position: 'absolute', inset: -20, borderRadius: 'inherit',
        background: 'rgba(200,168,75,0.35)',
        animation: '_xFlash 0.38s ease both',
        pointerEvents: 'none',
      }} />
      {/* particles */}
      {pts.map((p, i) => (
        <div key={i} style={{
          position: 'absolute',
          left: '50%', top: '50%',
          width: p.size, height: p.size,
          marginLeft: -p.size / 2, marginTop: -p.size / 2,
          borderRadius: p.shape === 'circle' ? '50%' : p.shape === 'square' ? 3 : '50%',
          background: p.shape === 'star' ? 'transparent' : p.color,
          // star via clip-path
          clipPath: p.shape === 'star'
            ? 'polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%)'
            : undefined,
          boxShadow: `0 0 8px ${p.color}, 0 0 16px ${p.color}44`,
          '--tx': `${p.tx}px`,
          '--ty': `${p.ty}px`,
          '--rot': p.rot,
          animation: `_xPart ${p.dur}s ${p.delay}s ease-out both`,
        }} />
      ))}
    </div>
  );
}

// ── GlowCard ──────────────────────────────────────────────────────────────────
// Shows spotlight on mouse move (desktop) AND on touch-start (mobile).
// Touch-start fires before scroll decision — so it works on all cards.
export function GlowCard({ children, color, style, onClick, className }) {
  const [glow, setGlow] = React.useState(null);
  const ref             = React.useRef(null);

  const update = (clientX, clientY) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setGlow({ x: clientX - r.left, y: clientY - r.top });
  };

  return (
    <div
      ref={ref}
      className={className}
      onClick={onClick}
      // Desktop: track continuously
      onMouseMove={e => update(e.clientX, e.clientY)}
      onMouseLeave={() => setGlow(null)}
      // Mobile: show glow at touch point immediately (before scroll takes over)
      onTouchStart={e => { const t = e.touches[0]; if (t) update(t.clientX, t.clientY); }}
      onTouchMove={e => { const t = e.touches[0]; if (t) update(t.clientX, t.clientY); }}
      onTouchEnd={() => setTimeout(() => setGlow(null), 220)}
      onTouchCancel={() => setGlow(null)}
      style={{ position: 'relative', ...style }}
    >
      {glow && (
        <div aria-hidden style={{
          position: 'absolute', inset: 0, borderRadius: 'inherit',
          pointerEvents: 'none', zIndex: 9,
          background: `radial-gradient(circle 160px at ${glow.x}px ${glow.y}px, ${color || 'rgba(200,168,75,0.28)'} 0%, transparent 65%)`,
          mixBlendMode: 'screen',
        }} />
      )}
      {children}
    </div>
  );
}

// ── TapButton ─────────────────────────────────────────────────────────────────
const GROW_MS    = 160;
const EXPLODE_MS = 1800;

export function TapButton({ children, onClick, style, className, disabled }) {
  const [phase, setPhase]       = React.useState('idle');
  const [particles, setParticles] = React.useState(false);
  const [toast, setToast]         = React.useState(false);
  const [popup, setPopup]         = React.useState(false);
  const nodeRef                   = React.useRef(null);
  const growT                     = React.useRef(null);
  const explodeT                  = React.useRef(null);

  // Reliable animation restart via DOM ref
  const bounce = () => {
    const el = nodeRef.current;
    if (!el) return;
    el.style.animation = 'none';
    el.getBoundingClientRect(); // force reflow
    el.style.animation = '_dpb 0.40s cubic-bezier(.34,1.56,.64,1) both';
    el.addEventListener('animationend', () => { if (el) el.style.animation = ''; }, { once: true });
  };

  const handleClick = e => {
    if (disabled || phase === 'exploding' || phase === 'growing') return;
    bounce();
    onClick?.(e);
  };

  const handlePD = () => {
    if (disabled) return;
    growT.current    = setTimeout(() => setPhase('hold'), GROW_MS);
    explodeT.current = setTimeout(async () => {
      setPhase('exploding');
      setParticles(true);
      awardCredit();
      const cnt = getXplCount();
      if (cnt < 2) { incXplCount(); setPopup(true); }
      else          { setToast(true); }
      setTimeout(() => {
        setParticles(false);
        setPhase('growing');
        setTimeout(() => setPhase('idle'), 1800);
      }, 420);
    }, EXPLODE_MS);
  };

  const cancel = () => {
    clearTimeout(growT.current);
    clearTimeout(explodeT.current);
    setPhase(p => p === 'hold' ? 'idle' : p);
  };

  const phaseStyle =
    phase === 'hold'      ? { transform: 'scale(1.09)', transition: 'transform 0.3s cubic-bezier(.34,1.56,.64,1)', boxShadow: '0 0 20px rgba(200,168,75,0.4)' }
  : phase === 'exploding' ? { animation: '_xBurst 0.42s ease-in both', transition: 'none' }
  : phase === 'growing'   ? { animation: '_xGrow 1.8s cubic-bezier(.34,1.56,.64,1) both', transition: 'none' }
  :                         { transform: 'scale(1)', transition: 'transform 0.28s cubic-bezier(.34,1.56,.64,1)' };

  return (
    <>
      {popup && <DiscoveryPopup onClose={() => setPopup(false)} />}
      {toast && <CreditToast onDone={() => setToast(false)} />}
      <div
        ref={nodeRef}
        className={`sl-tap${className ? ` ${className}` : ''}`}
        onClick={handleClick}
        onPointerDown={handlePD}
        onPointerUp={cancel}
        onPointerLeave={cancel}
        onPointerCancel={cancel}
        style={{
          cursor: disabled ? 'default' : 'pointer',
          display: 'inline-block',
          position: 'relative',
          opacity: disabled ? 0.5 : 1,
          ...phaseStyle,
          ...style,
        }}
      >
        {particles && <Particles />}
        {children}
      </div>
    </>
  );
}
